import { useEffect, useState } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { Calendar, Clock, Video, Check, ArrowUpRight, ArrowDown, GraduationCap, Loader2, PartyPopper, Star } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { Reveal, Eyebrow } from "@/components/bits";

const STRIPE_URL = "https://buy.stripe.com/28E4gB7I45q39KU6cBeME02";
// December 12, 2026, 10:00 AM WAT (UTC+1)
const MASTERCLASS_AT = Date.UTC(2026, 11, 12, 9, 0, 0);

const WHO = [
  { title: "Dietitians", desc: "Registered dietitians ready to grow beyond the job title." },
  { title: "Nutritionists", desc: "Practising nutritionists building authority and reach." },
  { title: "Nutrition Students", desc: "Students positioning early for global careers." },
];

const LEARN = [
  { n: "01", t: "Professional Positioning", d: "Define what you want to be known for beyond your job title." },
  { n: "02", t: "Visibility & Personal Brand", d: "Use LinkedIn, content, speaking, networking and professional platforms strategically." },
  { n: "03", t: "Leadership Readiness", d: "Build the skills, evidence and experience that strengthen your case for bigger roles." },
  { n: "04", t: "Global Career Opportunities", d: "Position your experience for international organisations, leadership roles, collaborations and career opportunities." },
  { n: "05", t: "Career Evidence & Portfolio", d: "Learn what to document: impact, projects, leadership, publications, media and professional recognition." },
  { n: "06", t: "Your 90-Day Career Growth Plan", d: "Leave with a practical roadmap for becoming more visible and strategically positioned." },
];

const LEAVE = [
  "Clearer positioning",
  "Career narrative",
  "Visibility strategy",
  "Global opportunity map",
  "Evidence checklist",
  "90-day action plan",
];

function scrollToRegister(e) {
  e?.preventDefault();
  document.getElementById("masterclass-register")?.scrollIntoView({ behavior: "smooth", block: "center" });
}

function ScrollRegisterButton({ testid, className = "" }) {
  return (
    <a
      href="#masterclass-register"
      onClick={scrollToRegister}
      data-testid={testid}
      className={`group inline-flex items-center justify-center gap-2 bg-tnn-purple text-white font-bold text-sm tracking-wide px-8 py-4 rounded-full transition-colors duration-200 hover:bg-tnn-deep ${className}`}
    >
      REGISTER & PAY
      <ArrowDown className="w-4 h-4 transition-transform duration-200 group-hover:translate-y-0.5" />
    </a>
  );
}

function Countdown() {
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const diff = MASTERCLASS_AT - now;
  if (diff <= 0) return null;
  const units = [
    [Math.floor(diff / 86400000), "Days"],
    [Math.floor((diff % 86400000) / 3600000), "Hours"],
    [Math.floor((diff % 3600000) / 60000), "Mins"],
    [Math.floor((diff % 60000) / 1000), "Secs"],
  ];
  return (
    <div data-testid="masterclass-countdown" className="flex gap-2.5">
      {units.map(([v, label]) => (
        <div key={label} className="bg-white border border-tnn-ink/8 rounded-2xl px-4 py-3 text-center min-w-[70px]">
          <p className="font-display text-2xl font-bold text-tnn-purple tabular-nums" data-testid={`countdown-${label.toLowerCase()}`}>
            {String(v).padStart(2, "0")}
          </p>
          <p className="mt-0.5 text-[10px] font-bold tracking-[0.18em] uppercase text-tnn-muted">{label}</p>
        </div>
      ))}
    </div>
  );
}

function PricingCard() {
  const [form, setForm] = useState({ name: "", email: "" });
  const [state, setState] = useState("idle");
  const [seats, setSeats] = useState(null);

  useEffect(() => {
    api
      .get("/masterclass/seats")
      .then((r) => setSeats(r.data))
      .catch(() => setSeats(null));
  }, []);

  const onPay = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please add your name and email so we can hold your seat.");
      return;
    }
    setState("saving");
    try {
      const { data } = await api.post("/masterclass/leads", { ...form, origin_url: window.location.origin });
      localStorage.setItem("tnn_mc_registered", "1");
      if (data.checkout_url) {
        window.location.assign(data.checkout_url);
        return;
      }
    } catch {
      // payment should never be blocked by the capture step
    }
    setState("sent");
    window.open(STRIPE_URL, "_blank", "noopener");
  };

  const inputCls =
    "w-full bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-sm text-white placeholder:text-white/40 focus:outline-none focus:border-tnn-lilac focus:ring-2 focus:ring-tnn-lilac/20 transition-[border-color,box-shadow] duration-200";

  return (
    <div id="masterclass-register" className="bg-white/5 border border-white/10 rounded-3xl p-8 lg:p-10 scroll-mt-28">
      <p className="text-xs font-bold tracking-[0.22em] uppercase text-tnn-lilac">Price</p>
      <div className="mt-6 space-y-5">
        <div className={`flex items-baseline justify-between ${seats?.founding_open === false ? "" : "border-b border-white/10 pb-5"}`}>
          <span className="text-white/70 text-sm">
            Regular
            {seats?.founding_open === false && (
              <span className="block text-xs text-tnn-lilac mt-0.5" data-testid="price-regular-note">Current rate</span>
            )}
          </span>
          <span className="font-display text-3xl font-bold text-white" data-testid="price-regular">$50</span>
        </div>
        <div className={`flex items-baseline justify-between ${seats?.founding_open === false ? "opacity-50" : ""}`}>
          <span className="text-white/70 text-sm">
            Founding Cohort
            {seats?.founding_open === false ? (
              <span className="block text-xs font-bold text-red-300 mt-0.5" data-testid="seats-urgency">Cohort full</span>
            ) : (
              <span className="block text-xs text-tnn-lilac mt-0.5" data-testid="seats-urgency">Limited seats available</span>
            )}
          </span>
          <span
            className={`font-display text-3xl font-bold text-tnn-lilac ${seats?.founding_open === false ? "line-through" : ""}`}
            data-testid="price-founding"
          >
            $35
          </span>
        </div>
      </div>

      {state === "sent" ? (
        <div className="mt-8 bg-white/10 border border-tnn-lilac/30 rounded-2xl p-5 text-center" data-testid="register-sent">
          <PartyPopper className="w-6 h-6 text-tnn-lilac mx-auto" />
          <p className="mt-3 text-sm text-white font-semibold">Your seat is being held, {form.name.split(" ")[0]}.</p>
          <p className="mt-1 text-xs text-white/60 leading-relaxed">
            Complete your payment in the Stripe tab that just opened. If it didn't open, use the button below.
          </p>
          <a
            href={STRIPE_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="register-reopen"
            className="mt-4 inline-flex items-center gap-2 bg-tnn-purple text-white font-bold text-xs tracking-wide px-6 py-3 rounded-full hover:bg-white hover:text-tnn-deep transition-colors duration-200"
          >
            Open Payment <ArrowUpRight className="w-3.5 h-3.5" />
          </a>
        </div>
      ) : (
        <form onSubmit={onPay} className="mt-8 space-y-3" data-testid="register-form">
          <input
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            placeholder="Full name"
            required
            data-testid="register-name-input"
            className={inputCls}
          />
          <input
            type="email"
            value={form.email}
            onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
            placeholder="Email address"
            required
            data-testid="register-email-input"
            className={inputCls}
          />
          <button
            type="submit"
            disabled={state === "saving"}
            data-testid="masterclass-register-pay"
            className="group w-full inline-flex items-center justify-center gap-2 bg-tnn-purple text-white font-bold text-sm tracking-wide px-8 py-4 rounded-full transition-colors duration-200 hover:bg-white hover:text-tnn-deep disabled:opacity-60"
          >
            {state === "saving" ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" /> HOLD ON
              </>
            ) : (
              <>
                REGISTER & PAY
                <ArrowUpRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </>
            )}
          </button>
          <p className="text-center text-xs text-white/50">Secure payment via Stripe. We hold your details in case checkout is interrupted.</p>
        </form>
      )}
    </div>
  );
}

function Testimonials() {
  const [items, setItems] = useState(null);

  useEffect(() => {
    api
      .get("/masterclass/testimonials")
      .then((r) => setItems(r.data.testimonials))
      .catch(() => setItems([]));
  }, []);

  if (!items || items.length === 0) return null;

  return (
    <section className="bg-tnn-warm py-16 lg:py-24" data-testid="masterclass-testimonials">
      <div className="max-w-6xl mx-auto px-6">
        <Reveal>
          <Eyebrow>What Attendees Say</Eyebrow>
        </Reveal>
        <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {items.map((t, i) => (
            <Reveal key={`${t.name}-${i}`} delay={i * 0.06}>
              <figure className="bg-white border border-tnn-ink/6 rounded-3xl p-7 h-full flex flex-col">
                <div className="flex gap-0.5" data-testid={`testimonial-stars-${i}`}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <Star
                      key={n}
                      className={`w-4 h-4 ${t.rating >= n ? "text-tnn-purple fill-tnn-purple" : "text-tnn-ink/15"}`}
                    />
                  ))}
                </div>
                <blockquote className="mt-4 font-display text-lg text-tnn-ink leading-relaxed flex-1" data-testid={`testimonial-quote-${i}`}>
                  "{t.testimonial}"
                </blockquote>
                <figcaption className="mt-4 text-xs font-bold tracking-[0.18em] uppercase text-tnn-purple">
                  {t.name}
                </figcaption>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Masterclass() {
  const { hash } = useLocation();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const cancelled = params.get("cancelled");
  const [paid, setPaid] = useState(false);

  useEffect(() => {
    if (hash === "#masterclass-register") {
      const t = setTimeout(() => scrollToRegister(), 600);
      return () => clearTimeout(t);
    }
  }, [hash]);

  useEffect(() => {
    if (!cancelled) return;
    const t = setTimeout(() => {
      toast("Checkout was cancelled. Your details are saved, so you can finish any time from this page.");
    }, 100);
    return () => clearTimeout(t);
  }, [cancelled]);

  useEffect(() => {
    if (!sessionId) return;
    let tries = 0;
    let timer;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPaid(true);
          localStorage.setItem("tnn_mc_registered", "1");
          return;
        }
      } catch {
        // keep polling quietly
      }
      if (++tries < 12) timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [sessionId]);

  return (
    <main data-testid="masterclass-page">
      {paid && (
        <div className="bg-tnn-soft border-b border-tnn-purple/20" data-testid="payment-confirmed">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
            <span className="w-7 h-7 rounded-full bg-tnn-purple flex items-center justify-center shrink-0">
              <Check className="w-4 h-4 text-white" />
            </span>
            <p className="text-sm text-tnn-ink">
              <span className="font-bold">Payment confirmed.</span> You are on the list for December 12. Watch your
              inbox for the joining link closer to the date.
            </p>
          </div>
        </div>
      )}
      {!paid && sessionId && (
        <div className="bg-tnn-soft border-b border-tnn-purple/20" data-testid="payment-verifying">
          <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
            <Loader2 className="w-4 h-4 text-tnn-purple animate-spin shrink-0" />
            <p className="text-sm text-tnn-ink">
              <span className="font-bold">Confirming your payment…</span> This takes a few seconds.
            </p>
          </div>
        </div>
      )}
      <section className="bg-tnn-warm">
        <div className="max-w-6xl mx-auto px-6 pt-36 lg:pt-44 pb-16 lg:pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <Reveal>
                <Eyebrow>Career Growth Masterclass</Eyebrow>
              </Reveal>
              <Reveal delay={0.08}>
                <h1 className="mt-5 font-display text-4xl sm:text-5xl font-bold tracking-tight text-tnn-ink leading-[1.1]" data-testid="masterclass-title">
                  Positioning for <span className="text-tnn-purple">Global Leadership</span>
                </h1>
                <p className="mt-4 text-lg text-tnn-muted leading-relaxed">
                  How Nutrition Professionals Can Build Visibility, Influence & Career Opportunities
                </p>
              </Reveal>
              <Reveal delay={0.16} className="mt-7 flex flex-wrap gap-2.5">
                {[
                  { icon: Calendar, label: "December 12, 2026" },
                  { icon: Clock, label: "10:00 AM – 1:00 PM WAT" },
                  { icon: Video, label: "Live Online" },
                ].map((c, i) => (
                  <span
                    key={i}
                    data-testid={`masterclass-chip-${i}`}
                    className="inline-flex items-center gap-2 text-xs font-semibold px-4 py-2.5 rounded-full bg-white border border-tnn-ink/8 text-tnn-ink"
                  >
                    <c.icon className="w-3.5 h-3.5 text-tnn-purple" /> {c.label}
                  </span>
                ))}
              </Reveal>
              <Reveal delay={0.22} className="mt-8">
                <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-tnn-purple mb-3">Doors open in</p>
                <Countdown />
              </Reveal>
              <Reveal delay={0.26} className="mt-8">
                <ScrollRegisterButton testid="masterclass-register-hero" />
              </Reveal>
            </div>
            <Reveal delay={0.12}>
              <div className="bg-white border border-tnn-ink/8 rounded-3xl p-4 shadow-[0_16px_44px_rgba(40,0,40,0.07)]">
                <img
                  src="/assets/masterclass-flyer.png"
                  alt="Positioning for Global Leadership masterclass flyer"
                  data-testid="masterclass-flyer"
                  className="w-full rounded-2xl"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24" data-testid="masterclass-invite">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <Reveal>
            <Eyebrow>A Personal Invite</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-tnn-ink leading-[1.15]">
              Hear it from Tito.
            </h2>
            <p className="mt-3 text-base text-tnn-muted leading-relaxed max-w-xl mx-auto">
              A short message from your host on why this masterclass exists, and who it was built for.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-10 bg-tnn-warm border border-tnn-ink/8 rounded-3xl p-4 shadow-[0_16px_44px_rgba(40,0,40,0.07)]">
              <video
                src="/assets/masterclass-invite.mp4"
                poster="/assets/masterclass-poster.png"
                controls
                preload="none"
                playsInline
                data-testid="masterclass-invite-video"
                className="w-full rounded-2xl"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24 pt-0 lg:pt-0" data-testid="masterclass-who">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Who It's For</Eyebrow>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-5">
            {WHO.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.07}>
                <div className="bg-tnn-warm border border-tnn-ink/6 rounded-3xl p-8 h-full text-center transition-colors duration-300 hover:border-tnn-purple/30">
                  <span className="w-10 h-10 rounded-full bg-tnn-soft inline-flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-tnn-purple" />
                  </span>
                  <h3 className="mt-4 font-display text-xl font-semibold text-tnn-ink">{w.title}</h3>
                  <p className="mt-2 text-sm text-tnn-muted leading-relaxed">{w.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-tnn-warm py-16 lg:py-24" data-testid="masterclass-learn">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <Eyebrow>What You'll Learn</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-tnn-ink max-w-2xl leading-[1.15]">
              Six building blocks for a global career.
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {LEARN.map((l, i) => (
              <Reveal key={l.n} delay={i * 0.05}>
                <div className="bg-white border border-tnn-ink/6 rounded-3xl p-7 h-full transition-colors duration-300 hover:border-tnn-purple/30">
                  <span className="text-xs font-bold text-tnn-purple/50">{l.n}</span>
                  <h3 className="mt-3 font-display text-lg font-semibold text-tnn-ink">{l.t}</h3>
                  <p className="mt-2 text-sm text-tnn-muted leading-relaxed">{l.d}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24" data-testid="masterclass-leave">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <Eyebrow>What You'll Leave With</Eyebrow>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {LEAVE.map((item, i) => (
              <Reveal key={item} delay={i * 0.05}>
                <div className="flex items-center gap-3 bg-tnn-warm border border-tnn-ink/6 rounded-2xl px-5 py-4">
                  <span className="w-6 h-6 rounded-full bg-tnn-purple flex items-center justify-center shrink-0">
                    <Check className="w-3.5 h-3.5 text-white" />
                  </span>
                  <p className="font-semibold text-sm text-tnn-ink">{item}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Testimonials />

      <section className="bg-tnn-warm py-16 lg:py-24" data-testid="masterclass-details">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <div className="bg-tnn-deep rounded-[2rem] px-8 py-12 lg:p-16">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                <div>
                  <p className="text-xs font-bold tracking-[0.22em] uppercase text-tnn-lilac">Masterclass Details</p>
                  <div className="mt-6 space-y-4">
                    <p className="flex items-center gap-3 text-white text-lg font-display font-semibold">
                      <Calendar className="w-5 h-5 text-tnn-lilac" /> December 12, 2026
                    </p>
                    <p className="flex items-center gap-3 text-white text-lg font-display font-semibold">
                      <Clock className="w-5 h-5 text-tnn-lilac" /> 10:00 AM – 1:00 PM WAT
                    </p>
                    <p className="flex items-center gap-3 text-white text-lg font-display font-semibold">
                      <Video className="w-5 h-5 text-tnn-lilac" /> Live Online
                    </p>
                  </div>
                  <p className="mt-10 font-display italic text-xl text-tnn-lilac leading-relaxed max-w-sm" data-testid="masterclass-quote">
                    "Your qualification opens the door. Your positioning determines how far your career can travel."
                  </p>
                </div>
                <PricingCard />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24" data-testid="masterclass-host">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <Reveal>
              <div className="bg-tnn-warm border border-tnn-ink/8 rounded-3xl p-4">
                <img
                  src="/assets/masterclass-poster.png"
                  alt="Tito Ipinmoye, host of Positioning for Global Leadership"
                  data-testid="masterclass-poster"
                  loading="lazy"
                  className="w-full rounded-2xl"
                />
              </div>
            </Reveal>
            <div>
              <Reveal>
                <Eyebrow>Your Host</Eyebrow>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-tnn-ink leading-[1.15]">
                  Tito Ipinmoye, RD
                </h2>
                <p className="mt-4 text-[15px] text-tnn-muted leading-relaxed max-w-md">
                  Healthcare innovation executive, product leader and clinical dietitian. Founder of The Nutrition Network
                  Africa, working at the intersection of nutrition, digital health, AI and healthcare transformation.
                </p>
                <div className="mt-8">
                  <ScrollRegisterButton testid="masterclass-register-host" />
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
